// "Version uploaded" popup — shown after the first successful upload of a
// session in the Generation view (mounted in Generation/index.tsx, triggered
// by the session store's `sessionUploads` counter going 0 → 1), once per
// browser with the 7-day expiry from lib/onceFlag.ts. The wording stays
// neutral on purpose: a returning talent sees it again after the first
// upload of a new session, where "your first version" would be wrong.
// Same chrome and rules as WelcomeHowTo: the flag is written ONLY on the
// "Okay" click, no backdrop / ESC close, so the single CTA is the only way
// out and the flag is guaranteed to be set.

import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { markFirstUploadHintSeen } from '@/lib/firstUploadHint';
import styles from './FirstUploadHint.module.css';

interface Props {
  onDismiss(): void;
}

export function FirstUploadHint({ onDismiss }: Props) {
  const [closing, setClosing] = useState(false);

  const handleOkay = () => {
    markFirstUploadHintSeen();
    setClosing(true);
    // Let the fade-out play before unmounting.
    window.setTimeout(onDismiss, 180);
  };

  return (
    <div
      className={`${styles.backdrop} ${closing ? styles.closing : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label="Version uploaded"
    >
      <div className={styles.panel}>
        <i className={`ph-duotone ph-cloud-check ${styles.icon}`} aria-hidden="true" />
        <h2 className={styles.title}>Version uploaded!</h2>
        <p className={styles.text}>
          This asset is finished. You can continue with the next asset in the
          sidebar on the left, or upload more versions for this asset if you
          wish.
        </p>
        <div className={styles.actions}>
          <Button variant="primary" onClick={handleOkay}>
            Okay
          </Button>
        </div>
      </div>
    </div>
  );
}
